import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {spacing} from '../constants';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import LegalContent from './LegalContent';

type Tab = 'faq' | 'privacy' | 'terms';

interface LegalInfoModalProps {
  visible: boolean;
  onClose: () => void;
  initialTab?: Tab;
  showFaq?: boolean; // FAQ might not be relevant in settings
}

export default function LegalInfoModal({
  visible,
  onClose,
  initialTab = 'faq',
  showFaq = true,
}: LegalInfoModalProps) {
  const {t} = useTranslation();
  const {width} = useWindowDimensions();
  const isMobile = width < 768;
  const [activeTab, setActiveTab] = useState<Tab>(showFaq ? initialTab : 'privacy');

  // Update active tab when initialTab changes (e.g., when user clicks different footer links)
  useEffect(() => {
    if (visible) {
      setActiveTab(showFaq ? initialTab : (initialTab === 'faq' ? 'privacy' : initialTab));
    }
  }, [visible, initialTab, showFaq]);

  const tabs: {key: Tab; label: string}[] = showFaq
    ? [
        {key: 'faq', label: t('home.faq.title')},
        {key: 'privacy', label: t('home.privacy.title')},
        {key: 'terms', label: t('home.terms.title')},
      ]
    : [
        {key: 'privacy', label: t('home.privacy.title')},
        {key: 'terms', label: t('home.terms.title')},
      ];

  const renderFaqContent = () => (
    <>
      <View style={styles.section}>
        <Text style={styles.question}>{t('home.faq.q1')}</Text>
        <Text style={styles.answer}>{t('home.faq.a1')}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.question}>{t('home.faq.q2')}</Text>
        <Text style={styles.answer}>{t('home.faq.a2')}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.question}>{t('home.faq.q3')}</Text>
        <Text style={styles.answer}>{t('home.faq.a3')}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.question}>{t('home.faq.q4')}</Text>
        <Text style={styles.answer}>{t('home.faq.a4')}</Text>
      </View>
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={onClose} />
        <View style={[
          styles.container,
          isMobile ? styles.containerMobile : styles.containerDesktop,
        ]}>
          {/* Close Button */}
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            activeOpacity={0.8}>
            <Text style={styles.closeIcon}>close</Text>
          </TouchableOpacity>

          {/* Tabs */}
          <View style={styles.tabContainer}>
            {tabs.map(tab => (
              <TouchableOpacity
                key={tab.key}
                style={[styles.tab, activeTab === tab.key && styles.tabActive]}
                onPress={() => setActiveTab(tab.key)}
                activeOpacity={0.8}>
                <Text
                  style={[
                    styles.tabText,
                    activeTab === tab.key && styles.tabTextActive,
                  ]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {/* Content */}
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}>
            {activeTab === 'faq' && renderFaqContent()}
            {activeTab === 'privacy' && <LegalContent type="privacy" />}
            {activeTab === 'terms' && <LegalContent type="terms" />}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backdrop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  },
  container: {
    backgroundColor: 'rgba(255, 250, 245, 0.98)',
    // @ts-ignore - web-specific style
    backdropFilter: 'blur(20px)',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 10},
    shadowOpacity: 0.3,
    shadowRadius: 30,
    elevation: 20,
  },
  containerMobile: {
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    borderRadius: 0,
  },
  containerDesktop: {
    width: '80%',
    maxWidth: 900,
    height: '90%',
    borderRadius: 24,
  },
  closeButton: {
    position: 'absolute',
    top: spacing.md,
    right: spacing.md,
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeIcon: {
    fontFamily: MATERIAL_ICONS_FONT,
    fontSize: 20,
    color: '#666',
  },
  tabContainer: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
    paddingBottom: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  tab: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 8,
    backgroundColor: 'transparent',
  },
  tabActive: {
    backgroundColor: '#ee8c2b',
  },
  tabText: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
  },
  tabTextActive: {
    color: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: spacing.lg,
    paddingTop: spacing.md,
  },
  section: {
    marginBottom: spacing.lg,
  },
  question: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 16,
    fontWeight: '600',
    color: '#221910',
    marginBottom: spacing.xs,
  },
  answer: {
    fontFamily: 'Plus Jakarta Sans, sans-serif',
    fontSize: 15,
    fontWeight: '400',
    color: '#666',
    lineHeight: 22,
  },
});
